import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import { AJAX_URL } from '../../../config/index.js';

// layouts
import Header from './Header';
import MainView from './MainView';
import Sidebar from './Sidebar';

// ux
import LoadingAnimation from '../ux/LoadingAnimation.js';
import ModalContainer from '../ux/ModalContainer.js';

// custom hooks
import useAuth from '../hooks/useAuth';
import useProjects from '../hooks/useProjects';
import useFuses from '../hooks/useFuses';
import usePitches from '../hooks/usePitches';

// utils
import ScrollToTop from '../utils/ScrollToTop.js';

// helpers
import { wlMakeFuselightRequest } from '../helpers';

const objectArrayToString = (array) => {
  if (!Array.isArray(array)) {
    return '';
  }

  let string = '';
  array.forEach((item) => {
    // Loop through each property in the object
    for (const [key, value] of Object.entries(item)) {
      string += `${key}: ${value}, `;
    }
    string += '; '; // End of object, separate with semicolon for clarity
  });
  return string;
};

const MainLayout = ({ fuseShareId }) => {
  const history = useHistory();
  // const location = useLocation();

  const {
    isUserLoggedIn,
    userStatusLoading,
    userName,
    handleUserLoggedIn,
    handleSignOut
  } = useAuth();
  const [inputs, setInputs] = useState({
    project_id: '',
    brand_name: '',
    celeb_name: '',
    context: '',
    idea: '',
    message: ''
  });
  const {
    projects,
    // setProjects,
    projectLoading,
    setProjectLoading,
    fetchProjects,
    isFetchingProjects,
    updateProject,
    deleteProject,
    deleteProjects
  } = useProjects();
  const {
    fuses,
    // setFuses,
    fetchFuses,
    isFetchingFuses,
    updateFuse,
    deleteFuse,
    deleteFuses
  } = useFuses();
  const {
    pitches,
    // setPitches,
    fetchPitches,
    isFetchingPitches,
    updatePitch,
    deletePitch,
    deletePitches
  } = usePitches();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalInfo, setModalInfo] = useState({});
  const [projectThread, setProjectThread] = useState({});
  const [projectFuseList, setProjectFuseList] = useState([]);
  const [projectConvo, setProjectConvo] = useState({});
  const [updatingProjectList, setUpdatingProjectList] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [fuseLoadingList, setFuseLoadingList] = useState([]);
  const [fuseLoading, setFuseLoading] = useState(false);
  const [fuseSavedData, setFuseSavedData] = useState({});
  const [fuseLoadingData, setFuseLoadingData] = useState({});
  const [loadingPitchesArray, setLoadingPitchesArray] = useState([]);
  const [deckLink, setDeckLink] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState({
    questionOne: true,
    questionTwo: false,
    questionThree: false
  });
  const [backInfo, setBackInfo] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (fuseShareId) {
      handleUserLoggedIn('shared_user');
      fetchFuse(fuseShareId.ID);
    }
  }, [fuseShareId]);

  // on component mount load initial data of api endpoints, projects and fuses
  useEffect(() => {
    const initialLoadData = async () => {
      await Promise.all([fetchProjects(), fetchFuses(), fetchPitches()]);
    };

    const savedInputs = JSON.parse(localStorage.getItem('fl_inputs'));
    if (savedInputs) {
      setInputs(savedInputs);
    }

    initialLoadData();
  }, []);

  // maintain loading states while navigating
  useEffect(() => {
    if (
      location.pathname === '/project' &&
      inputs.project_id &&
      projectFuseList.length > 0
    ) {
      console.log('projectFuseList:', projectFuseList);
      const loading = projectFuseList
        .filter((fuse) => {
          // Consider fuse still loading if fuse_ideas is missing or empty
          return !fuse.fuse_ideas || fuse.fuse_ideas.length === 0;
        })
        .map((fuse) => ({
          project_id: inputs.project_id,
          idea_id: fuse.idea_id
        }));

      setFuseLoadingList(loading);
    }
  }, [location.pathname, inputs.project_id, projectFuseList]);

  const handleFullUserLoggedIn = (username) => {
    handleUserLoggedIn(username);
    handleViewChange('launchpad');
    fetchProjects();
    fetchFuses();
    fetchPitches();
  };

  const onRegisterSuccess = handleFullUserLoggedIn;

  const handleFullSignOut = async () => {
    console.log('handleFullSignOut');
    await handleSignOut();
    console.log('signed out, resetting view');
    handleViewChange('launchpad');
  };

  const toggleSidebar = () => {
    setSidebarOpen((prevState) => !prevState);
    setMobileSidebarOpen((prevState) => !prevState);
  };

  const handleViewChange = (view) => {
    localStorage.removeItem('fl_inputs');
    setInputs({
      project_id: '',
      brand_name: '',
      celeb_name: '',
      context: '',
      idea: '',
      message: ''
    });
    handleQuestionChange('questionOne');
    setHelpOpen(false);
    if (view === 'launchpad') {
      setProjectFuseList([]);
    }
    setMobileSidebarOpen(false);
    console.log('handleViewChange', view);
    console.log('history', history);
    history.push(`/${view}`);
  };

  const handleQuestionChange = (question) => {
    setCurrentQuestion({
      questionOne: false,
      questionTwo: false,
      questionThree: false,
      [question]: true
    });
  };

  const handleOpenModal = (modalType, modalInfo) => {
    setModalOpen(true);
    setModalType(modalType);
    setModalInfo(modalInfo);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType('');
    setModalInfo({});
  };

  const handleInputsChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleCreateProject = async () => {
    console.log('Creating project...');
    // Check if user is logged in
    if (!isUserLoggedIn) {
      // Save inputs to local storage
      localStorage.setItem('fl_inputs', JSON.stringify(inputs));

      // setCurrentView({
      //   launchpad: false,
      //   fuse: false,
      //   project: false,
      //   launchplans: false,
      //   fusions: false,
      //   pitches: false,
      //   login: true,
      //   about: false,
      //   tutorial: false
      // });

      handleViewChange('login');
      return;
    }
    // Set loading project
    setProjectLoading(true);
    // Switch to project view
    handleViewChange('project');

    try {
      console.log('Creating project with inputs:', inputs);
      const resInitial = await axios.post(
        '/wp-json/custom/v1/proxy',
        {
          action: 'createProject',
          data: inputs
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 120000 // 120 seconds in ms
        }
      );
      console.log('Raw API result:', resInitial);
      if (!resInitial.data || !resInitial.data.response) {
        console.log('No response from API');
        setProjectLoading(false);
        alert(
          resInitial.data?.errorMessage ||
            'Something went wrong. Please try again.'
        );
        handleViewChange('launchpad');
        return;
      } else {
        console.log('createProject response:', resInitial.data);
        const projectResponse = resInitial.data.response;

        // Add custom idea if inputs.idea is not empty or null
        if (inputs.idea) {
          const customIdea = {
            title: `${inputs.brand_name} x ${inputs.celeb_name}`,
            partner: inputs.celeb_name,
            desc: inputs.idea,
            short_desc: ''
          };
          projectResponse.idea = [customIdea, ...projectResponse.idea];
        }

        setProjectThread(resInitial.data.thread_id);
        setProjectConvo({
          messages: [projectResponse] || [
            {
              opening_text: '',
              idea: [],
              closing_text: ''
            }
          ]
        });
        publishNewProject(resInitial.data);
        setProjectLoading(false);

        // Clear local storage
        localStorage.removeItem('fl_inputs');
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      setProjectLoading(false);
    }
  };

  const publishNewProject = async (projectData) => {
    const dataToSend = {
      brand_name: inputs.brand_name,
      celeb_name: inputs.celeb_name,
      thread_id: projectData.thread_id,
      context: inputs.context,
      opening_text: projectData.response.opening_text,
      // Serialize the array of objects into a JSON string
      initial_ideas: JSON.stringify(projectData.response.idea)
    };
    try {
      const response = await fetch(`${AJAX_URL}fl_create_project`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams(dataToSend)
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const jsonResponse = await response.json();
      setInputs({ ...inputs, project_id: jsonResponse.data.ID });
      fetchProjects();
    } catch (error) {
      console.error('There was a problem with your fetch operation:', error);
    }
  };

  const handleUpdateProjectConvo = async (threadID) => {
    setLoadingMessage(true);

    // Prepare the user's message
    const userMessage = {
      opening_text: inputs.message,
      sender: 'user'
    };

    // Update conversation state with user's message
    setProjectConvo((prev) => ({
      ...prev,
      messages: [...prev.messages, userMessage]
    }));

    const dataToSend = {
      thread_id: threadID,
      text: inputs.message
    };

    setInputs({ ...inputs, message: '' });

    try {
      const resUpdate = await axios.post(
        '/wp-json/custom/v1/proxy',
        {
          action: 'updateProjectConvo',
          data: dataToSend
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (
        resUpdate.data.errorMessage ||
        !resUpdate.data ||
        !resUpdate.data.response
      ) {
        // Handle error...
        setLoadingMessage(false);
      } else {
        // setInputs({ ...inputs, message: '' });
        const { response } = resUpdate.data;
        const { opening_text, idea } = response || {};

        // Prepare the system's response message
        const systemMessage = {
          opening_text: opening_text,
          sender: 'system',
          idea: idea || []
        };

        // Update the conversation state with the system's response
        setProjectConvo((prev) => ({
          ...prev,
          messages: [...prev.messages, systemMessage]
        }));

        setLoadingMessage(false);

        // Prepare data to update the project
        const updateProjectData = {
          project_id: inputs.project_id,
          new_messages: JSON.stringify([userMessage, systemMessage])
        };

        updateProject(updateProjectData);
      }
    } catch (error) {
      // Handle error...
      console.error('Failed to update project convo:', error);
      setLoadingMessage(false);
    }
  };

  const fetchProject = async (project) => {
    setDeckLink('');
    setProjectLoading(true);
    handleViewChange('project');
    try {
      const response = await fetch(`${AJAX_URL}fl_fetch_project`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({ post_id: project.ID })
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const jsonResponse = await response.json();
      if (jsonResponse.success) {
        // Set the inputs
        setInputs({
          ...inputs,
          project_id: jsonResponse.data.ID,
          brand_name: jsonResponse.data.brand_name,
          celeb_name: jsonResponse.data.celeb_name,
          context: jsonResponse.data.context,
          idea: jsonResponse.data.idea
        });

        // Set the project conversation
        setProjectConvo({
          messages: jsonResponse.data.conversation_message || []
        });

        // Set other project data
        setProjectThread(jsonResponse.data.thread_id);
        setProjectFuseList(jsonResponse.data.fuse_list);
        setDeckLink(jsonResponse.data.pitch_link);

        setProjectLoading(false);
      } else {
        throw new Error('Success is false');
      }
    } catch (error) {
      console.error('Failed to fetch project:', error.message);
    }
  };

  // fetch fuse
  const fetchFuse = async (fuseID, backInfo) => {
    setFuseSavedData({});
    setFuseLoading(true);
    handleViewChange('fuse');
    setBackInfo(backInfo);
    try {
      const response = await fetch(`${AJAX_URL}fl_fetch_fuse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({ post_id: fuseID })
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const jsonResponse = await response.json();
      if (jsonResponse.success) {
        console.log('fetchFuse:', jsonResponse.data);
        setFuseSavedData(jsonResponse.data);
        setFuseLoading(false);
      } else {
        throw new Error('Success is false');
      }
    } catch (error) {
      console.error('Failed to fetch fuse:', error.message);
    }
  };

  // create fuse function
  const handleCreateFuse = async (idea, uniqueId) => {
    setUpdatingProjectList(true);
    const projectID = inputs.project_id;
    // publish fuse as custom post type
    const dataToSend = {
      brand_name: inputs.brand_name,
      celebrity_name: idea.partner,
      fuse_title: idea.title,
      idea_id: uniqueId
    };

    // update fuseLoadingList with project_id and idea_id
    setFuseLoadingList((prevFuseLoadingList) => [
      ...prevFuseLoadingList,
      { project_id: projectID, idea_id: uniqueId }
    ]);

    try {
      const response = await fetch(`${AJAX_URL}fl_create_fuse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams(dataToSend)
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const jsonResponse = await response.json();

      // update projectFuseList with new fuse
      setProjectFuseList([...projectFuseList, jsonResponse.data]);

      // add fuse to project list
      handleAddFuseToProject(jsonResponse.data.ID);

      setUpdatingProjectList(false);

      // fetch job id
      const jobId = await fetchJobId(inputs.brand_name, idea);

      // update fuse post with job id
      const dataToUpdate = {
        ID: jsonResponse.data.ID,
        job_id: jobId
      };
      updateFuse(dataToUpdate);

      // begin polling api for fuse data
      fetchFuseData(
        jobId,
        jsonResponse.data.ID,
        idea.desc,
        projectID,
        uniqueId,
        idea,
        inputs.brand_name
      );
    } catch (error) {
      console.error('Failed to create fuse:', error.message);
    }
  };

  // add fuse to project using inputs.project_id
  const handleAddFuseToProject = async (fuseID) => {
    const dataToSend = {
      fuse_id: fuseID,
      project_id: inputs.project_id
    };
    try {
      const response = await fetch(`${AJAX_URL}fl_add_fuse_to_project`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams(dataToSend)
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      // const jsonResponse = await response.json();
      fetchProjects();
    } catch (error) {
      console.error('There was a problem with your fetch operation:', error);
    }
  };

  // fetch job id
  const fetchJobId = async (brandName, idea) => {
    const dataToSend = {
      brand_name: brandName,
      celeb_name: idea.partner,
      collab_details: idea.desc
    };
    try {
      const res = await axios.post(
        '/wp-json/custom/v1/proxy',
        {
          action: 'createFuse',
          data: dataToSend
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return res.data.job_id;
    } catch (error) {
      console.error('Failed to create fuse:', error);
    }
  };

  const fetchFuseData = async (
    jobID,
    fuseID,
    fuseDesc,
    projectID,
    ideaID,
    idea,
    brandName,
    attemptNumber = 1
  ) => {
    const dataToSend = {
      job_id: jobID
    };
    const pollInterval = 5000; // Poll every 5 seconds
    const maxRetries = 12; // Retry for 1 minute
    const maxAttempts = 3; // Max attempts to fetch new job_id and restart polling
    let retries = 0;

    // Initialize updatedFuseData with initial values
    let updatedFuseData = {
      ID: fuseID,
      desc: fuseDesc
      // Add any other initial properties if needed
    };

    // Keep track of which data points have been received
    let receivedDataPoints = {
      model_results: false,
      historic_mentions: false,
      brand_geo: false,
      brand_timeseries: false,
      celeb_geo: false,
      celeb_timeseries: false,
      ai_images: false
    };

    const pollFuseResults = async () => {
      try {
        const res = await axios.post(
          '/wp-json/custom/v1/proxy',
          {
            action: 'getFuseResults',
            data: dataToSend
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        if (res.data && res.data.body && Array.isArray(res.data.body)) {
          res.data.body.forEach((item) => {
            const apiName = item.api_name;
            if (!receivedDataPoints[apiName]) {
              receivedDataPoints[apiName] = true;
              updateFuseWithData(apiName, item.data);
            }
          });

          const allDataReceived = Object.values(receivedDataPoints).every(
            (received) => received === true
          );

          if (allDataReceived) {
            // Remove from loading list
            setFuseLoadingList((prevFuseLoadingList) =>
              prevFuseLoadingList.filter(
                (fuse) =>
                  fuse.project_id !== projectID || fuse.idea_id !== ideaID
              )
            );
            return true; // Stop polling
          }
        }

        retries += 1;
        if (retries < maxRetries) {
          return new Promise((resolve) => {
            setTimeout(() => resolve(pollFuseResults()), pollInterval);
          });
        } else {
          // After maxRetries, check if model_results have been received
          if (
            !receivedDataPoints.model_results &&
            attemptNumber < maxAttempts
          ) {
            // Fetch new job_id
            const newJobId = await fetchJobId(brandName, idea);
            if (newJobId) {
              // Update the fuse post with new job_id
              const dataToUpdate = {
                ID: fuseID,
                job_id: newJobId
              };
              updateFuse(dataToUpdate);

              // Restart polling with new job_id and incremented attemptNumber
              return fetchFuseData(
                newJobId,
                fuseID,
                fuseDesc,
                projectID,
                ideaID,
                idea,
                brandName,
                attemptNumber + 1
              );
            } else {
              console.error('Failed to fetch new job_id');
              // Remove from loading list
              setFuseLoadingList((prevFuseLoadingList) =>
                prevFuseLoadingList.filter(
                  (fuse) =>
                    fuse.project_id !== projectID || fuse.idea_id !== ideaID
                )
              );
              return false;
            }
          } else {
            // Either model_results received or maxAttempts reached
            setFuseLoadingList((prevFuseLoadingList) =>
              prevFuseLoadingList.filter(
                (fuse) =>
                  fuse.project_id !== projectID || fuse.idea_id !== ideaID
              )
            );
            return false;
          }
        }
      } catch (error) {
        console.error('Error fetching fuse results:', error);
        setFuseLoadingList((prevFuseLoadingList) =>
          prevFuseLoadingList.filter(
            (fuse) => fuse.project_id !== projectID || fuse.idea_id !== ideaID
          )
        );
        return false;
      }
    };

    const updateFuseWithData = (apiName, data) => {
      switch (apiName) {
        case 'model_results':
          const {
            frequency = null,
            predicted_mention = null,
            predicted_reach = null,
            ignition_thrust = null,
            ignition_context = null
          } = data || {};

          updatedFuseData = {
            ...updatedFuseData,
            frequency: frequency,
            predicted_media_mentions: predicted_mention,
            predicted_impressions: predicted_reach,
            ignition_thrust: ignition_thrust,
            ignition_context: ignition_context
          };
          break;

        case 'historic_mentions':
          const { mentions: historic_mentions = null } = data || {};

          updatedFuseData = {
            ...updatedFuseData,
            avg_monthly_mentions: historic_mentions
          };
          break;

        case 'brand_geo':
          const { geo: brand_geo = null, subtitle: brand_geo_subtitle = null } =
            data || {};

          updatedFuseData = {
            ...updatedFuseData,
            brand_geo: objectArrayToString(brand_geo),
            brand_geo_subtitle: brand_geo_subtitle
          };
          break;

        case 'brand_timeseries':
          const { brand_timeseries = null } = data || {};

          updatedFuseData = {
            ...updatedFuseData,
            brand_timeseries: objectArrayToString(brand_timeseries)
          };
          break;

        case 'celeb_geo':
          const { geo: celeb_geo = null, subtitle: celeb_geo_subtitle = null } =
            data || {};

          updatedFuseData = {
            ...updatedFuseData,
            celeb_geo: objectArrayToString(celeb_geo),
            celeb_geo_subtitle: celeb_geo_subtitle
          };
          break;

        case 'celeb_timeseries':
          const { celeb_timeseries = null } = data || {};

          updatedFuseData = {
            ...updatedFuseData,
            celeb_timeseries: objectArrayToString(celeb_timeseries)
          };
          break;

        case 'ai_images':
          const newContentIdeas = data.Copy.map((copy, index) => ({
            idea_content: copy,
            idea_image_url: data.imageUrls[index] || '',
            pinned: false
          }));

          updatedFuseData = {
            ...updatedFuseData,
            fuse_ideas: [
              {
                fuse_idea: updatedFuseData.desc,
                content_ideas: newContentIdeas
              }
            ],
            fuse_meta_description: updatedFuseData.desc,
            fuse_meta_thumbnail_url: data.imageUrls[0] || ''
          };
          break;

        default:
          // Handle any other data points if necessary
          break;
      }

      // update loadingFuseData with new data, make it specific to ideaID
      setFuseLoadingData((prevFuseLoadingData) => ({
        ...prevFuseLoadingData,
        [ideaID]: updatedFuseData
      }));

      // Update the fuse with the new data
      updateFuse(updatedFuseData);

      // update projects so compare modal is accurate
      fetchProjects();
    };

    // Start the polling
    await pollFuseResults();
  };

  // handle fuse loading animation if navigating to other views
  // useEffect(() => {
  //   // Only recalculate when we're on the project view
  //   if (location.pathname !== '/project' || !inputs.project_id) return;

  //   const matchingProject = projects.find((p) => p.ID === inputs.project_id);

  //   if (matchingProject?.fuse_list) {
  //     const loading = matchingProject.fuse_list
  //       .filter((fuse) => !fuse.ignition_thrust) // Or another property you expect to exist when "complete"
  //       .map((fuse) => ({
  //         project_id: inputs.project_id,
  //         idea_id: fuse.idea_id
  //       }));

  //     setFuseLoadingList(loading);
  //   }
  // }, [location.pathname, inputs.project_id, projects]);

  // handle create pitch
  const createPitch = async (
    projectID,
    pitchTitle,
    selectedFuses,
    isDarkMode
  ) => {
    console.log('Creating pitch with:', projectID, pitchTitle, selectedFuses);
    // add projectID to loadingPitchesArray
    setLoadingPitchesArray([...loadingPitchesArray, projectID]);
    const input_list = selectedFuses.map((fuse) => fuse.ID);
    const dataToSend = {
      mode: isDarkMode ? 'dark' : 'light',
      input_list: input_list
    };
    console.log('createPitch dataToSend:', dataToSend);
    try {
      const res = await axios.post(
        '/wp-json/custom/v1/proxy',
        {
          action: 'createPitchDeck',
          data: dataToSend
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('createPitchDeck response:', res.data);
      // publish new pitch custom post type
      const publishPitchData = {
        job_id: res.data.job_id,
        pitch_title: pitchTitle,
        fuses: input_list
      };
      const pitchID = await handlePublishPitch(publishPitchData);
      getDeckUrl(projectID, pitchID, res.data.job_id);
    } catch (error) {
      console.error('Failed to create pitch deck:', error);
    }
  };

  // handle publish pitch
  const handlePublishPitch = async (publishPitchData) => {
    console.log('Publishing pitch with:', publishPitchData);
    try {
      // Create a URLSearchParams object
      const params = new URLSearchParams();
      params.append('job_id', publishPitchData.job_id);
      params.append('pitch_title', publishPitchData.pitch_title);

      // Append each fuse ID correctly
      publishPitchData.fuses.forEach((fuse) => {
        params.append('fuses[]', fuse); // Use 'fuses[]' to ensure PHP recognizes it as an array
      });

      const response = await fetch(`${AJAX_URL}fl_create_pitch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params.toString() // Convert params to string
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      console.log('publishPitch response:', response);
      const jsonResponse = await response.json();
      return jsonResponse.data.ID;
    } catch (error) {
      console.error('There was a problem with your fetch operation:', error);
    }
  };

  const getDeckUrl = async (projectID, pitchID, jobID) => {
    console.log('Getting deck URL with:', projectID, pitchID, jobID);
    const dataToSend = {
      job_id: jobID
    };

    const pollInterval = 20000;
    const maxRetries = 32; // 10 minutes
    let retries = 0;

    const pollDeckUrl = async () => {
      console.log('Polling deck URL with:', dataToSend);
      try {
        const res = await axios.post(
          '/wp-json/custom/v1/proxy',
          {
            action: 'getDeckUrl',
            data: dataToSend
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        console.log('getDeckUrl response:', res.data);
        if (res.data.body && res.data.body.data && res.data.body.data.url) {
          const deckUrl = res.data.body.data.url;
          const updatePitchData = {
            pitch_link: deckUrl
          };
          updatePitch(pitchID, updatePitchData);
          const updateProjectData = {
            project_id: projectID,
            pitch_list: [pitchID]
          };
          updateProject(updateProjectData);
          setDeckLink(deckUrl);
          setLoadingPitchesArray(
            loadingPitchesArray.filter((id) => id !== projectID)
          );
          fetchPitches();
        } else {
          if (retries < maxRetries) {
            retries++;
            setTimeout(pollDeckUrl, pollInterval);
          } else {
            console.error('Deck URL not found after maximum retries');
            setLoadingPitchesArray(
              loadingPitchesArray.filter((id) => id !== projectID)
            );
          }
        }
      } catch (error) {
        console.error('API error in getDeckUrl:', error);
        setLoadingPitchesArray(
          loadingPitchesArray.filter((id) => id !== projectID)
        );
      }
    };

    pollDeckUrl();
  };

  const renamePitch = (pitchID, newTitle) => {
    updatePitch(pitchID, { pitch_title: newTitle });
  };

  const renameFuse = (fuseID, newTitle) => {
    const fuse = fuses.find((f) => f.ID === fuseID);
    updateFuse({
      ...fuse,
      fuse_title: newTitle,
      meta_title: newTitle,
      og_title: newTitle,
      twitter_title: newTitle
    });
  };

  const renameProject = (projectID, newTitle) => {
    updateProject({ project_id: projectID, project_name: newTitle });
  };

  const pinImage = (fuseID, newContentIdeas) => {
    const pinned_idea = newContentIdeas.find((idea) => idea.pinned);
    const pinned_image = pinned_idea ? pinned_idea.idea_image_url : '';
    const fuse = fuses.find((f) => f.ID === fuseID);
    updateFuse({
      ...fuse,
      fuse_ideas: [
        {
          content_ideas: newContentIdeas
        }
      ],
      fuse_meta_thumbnail_url: pinned_image
    });
  };

  const retryFetchFuseData = async (jobID, fuseID, ideaID) => {
    // get project id from projects array if the fuseID is in the project fuse_list
    const project = projects.find((project) =>
      project.fuse_list.some((fuse) => fuse.ID === fuseID)
    );

    const projectID = project ? project.ID : null;

    // add fuse to fuseLoadingList
    setFuseLoadingList((prevFuseLoadingList) => [
      ...prevFuseLoadingList,
      { project_id: projectID, idea_id: ideaID }
    ]);

    const pollInterval = 10000; // Poll every 10 seconds
    const maxRetries = 12; // Retry for 2 minutes
    let retries = 0;

    // Initialize updatedFuseData with initial values
    let updatedFuseData = {
      ID: fuseID
      // Add any other initial properties if needed
    };

    // Keep track of which data points have been received
    let receivedDataPoints = {
      model_results: false,
      historic_mentions: false,
      brand_geo: false,
      brand_timeseries: false,
      celeb_geo: false,
      celeb_timeseries: false,
      ai_images: false
    };

    // polling function
    const pollFuseResults = async () => {
      try {
        const res = await axios.post(
          '/wp-json/custom/v1/proxy',
          {
            action: 'getFuseResults',
            data: { job_id: jobID }
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (res.data.body && Array.isArray(res.data.body)) {
          res.data.body.forEach((item) => {
            const apiName = item.api_name;
            if (!receivedDataPoints[apiName]) {
              receivedDataPoints[apiName] = true;
              updateFuseWithData(apiName, item.data);
            }
          });

          const allDataReceived = Object.values(receivedDataPoints).every(
            (received) => received === true
          );

          if (allDataReceived) {
            setFuseLoadingList((prevFuseLoadingList) =>
              prevFuseLoadingList.filter(
                (fuse) =>
                  fuse.project_id !== projectID || fuse.idea_id !== ideaID
              )
            );
            return true; // Stop polling
          }
        }

        retries += 1;
        if (retries < maxRetries) {
          return new Promise((resolve) => {
            setTimeout(() => resolve(pollFuseResults()), pollInterval);
          });
        } else {
          setFuseLoadingList((prevFuseLoadingList) =>
            prevFuseLoadingList.filter(
              (fuse) => fuse.project_id !== projectID || fuse.idea_id !== ideaID
            )
          );
          return false;
        }
      } catch (error) {
        console.error('Error fetching fuse results:', error);
        setFuseLoadingList((prevFuseLoadingList) =>
          prevFuseLoadingList.filter(
            (fuse) => fuse.project_id !== projectID || fuse.idea_id !== ideaID
          )
        );
        return false;
      }
    };

    const updateFuseWithData = (apiName, data) => {
      switch (apiName) {
        case 'model_results':
          const {
            frequency = null,
            predicted_mention = null,
            predicted_reach = null,
            ignition_thrust = null,
            ignition_context = null
          } = data || {};

          updatedFuseData = {
            ...updatedFuseData,
            frequency: frequency,
            predicted_media_mentions: predicted_mention,
            predicted_impressions: predicted_reach,
            ignition_thrust: ignition_thrust,
            ignition_context: ignition_context
          };
          break;

        case 'historic_mentions':
          const { mentions: historic_mentions = null } = data || {};

          updatedFuseData = {
            ...updatedFuseData,
            avg_monthly_mentions: historic_mentions
          };
          break;

        case 'brand_geo':
          const { geo: brand_geo = null, subtitle: brand_geo_subtitle = null } =
            data || {};

          updatedFuseData = {
            ...updatedFuseData,
            brand_geo: objectArrayToString(brand_geo),
            brand_geo_subtitle: brand_geo_subtitle
          };
          break;

        case 'brand_timeseries':
          const { brand_timeseries = null } = data || {};

          updatedFuseData = {
            ...updatedFuseData,
            brand_timeseries: objectArrayToString(brand_timeseries)
          };
          break;

        case 'celeb_geo':
          const { geo: celeb_geo = null, subtitle: celeb_geo_subtitle = null } =
            data || {};

          updatedFuseData = {
            ...updatedFuseData,
            celeb_geo: objectArrayToString(celeb_geo),
            celeb_geo_subtitle: celeb_geo_subtitle
          };
          break;

        case 'celeb_timeseries':
          const { celeb_timeseries = null } = data || {};

          updatedFuseData = {
            ...updatedFuseData,
            celeb_timeseries: objectArrayToString(celeb_timeseries)
          };
          break;

        case 'ai_images':
          const newContentIdeas = data.Copy.map((copy, index) => ({
            idea_content: copy,
            idea_image_url: data.imageUrls[index] || '',
            pinned: false
          }));

          updatedFuseData = {
            ...updatedFuseData,
            fuse_ideas: [
              {
                fuse_idea: updatedFuseData.desc,
                content_ideas: newContentIdeas
              }
            ],
            fuse_meta_description: updatedFuseData.desc,
            fuse_meta_thumbnail_url: data.imageUrls[0] || ''
          };
          break;

        default:
          // Handle any other data points if necessary
          break;
      }

      // update loadingFuseData with new data, make it specific to ideaID
      setFuseLoadingData((prevFuseLoadingData) => ({
        ...prevFuseLoadingData,
        [ideaID]: updatedFuseData
      }));

      // Update the fuse with the new data
      updateFuse(updatedFuseData);
    };

    // Start the polling
    await pollFuseResults();
  };

  // toggle help
  const toggleHelp = () => {
    setHelpOpen(!helpOpen);
  };

  // console log helpOpen
  useEffect(() => {
    console.log('helpOpen:', helpOpen);
  }, [helpOpen]);

  return (
    <>
      <ScrollToTop />
      <div id="fuselight_v5">
        <div className="fuselight-v5-main-layout">
          {userStatusLoading ? (
            <LoadingAnimation label="Checking User Authentication" />
          ) : (
            <>
              <Header
                userName={userName}
                handleViewChange={handleViewChange}
                handleSignOut={handleFullSignOut}
                // currentView={currentView}
                sidebarOpen={sidebarOpen}
                mobileSidebarOpen={mobileSidebarOpen}
                toggleSidebar={toggleSidebar}
                toggleHelp={toggleHelp}
                helpOpen={helpOpen}
              />
              {isUserLoggedIn && (
                <Sidebar
                  handleViewChange={handleViewChange}
                  // currentView={currentView}
                  sidebarOpen={sidebarOpen}
                  mobileSidebarOpen={mobileSidebarOpen}
                />
              )}
              <MainView
                projects={projects}
                fetchProject={fetchProject}
                isFetchingProjects={isFetchingProjects}
                fuses={fuses}
                fetchFuse={fetchFuse}
                isFetchingFuses={isFetchingFuses}
                pitches={pitches}
                isFetchingPitches={isFetchingPitches}
                fuseShareId={fuseShareId}
                handleViewChange={handleViewChange}
                // currentView={currentView}
                handleOpenModal={handleOpenModal}
                inputs={inputs}
                handleInputsChange={handleInputsChange}
                currentQuestion={currentQuestion}
                handleQuestionChange={handleQuestionChange}
                handleCreateProject={handleCreateProject}
                projectThread={projectThread}
                projectFuseList={projectFuseList}
                projectConvo={projectConvo}
                projectLoading={projectLoading}
                updatingProjectList={updatingProjectList}
                handleUpdateProjectConvo={handleUpdateProjectConvo}
                loadingMessage={loadingMessage}
                handleCreateFuse={handleCreateFuse}
                fuseLoadingList={fuseLoadingList}
                fuseLoading={fuseLoading}
                fuseSavedData={fuseSavedData}
                fuseLoadingData={fuseLoadingData}
                handleUserLoggedIn={handleFullUserLoggedIn}
                onRegisterSuccess={onRegisterSuccess}
                sidebarOpen={sidebarOpen}
                mobileSidebarOpen={mobileSidebarOpen}
                setSidebarOpen={setSidebarOpen}
                createPitch={createPitch}
                loadingPitchesArray={loadingPitchesArray}
                deckLink={deckLink}
                renamePitch={renamePitch}
                renameFuse={renameFuse}
                renameProject={renameProject}
                backInfo={backInfo}
                pinImage={pinImage}
                retryFetchFuseData={retryFetchFuseData}
                helpOpen={helpOpen}
                toggleHelp={toggleHelp}
              />
            </>
          )}
        </div>
        {modalOpen && (
          <ModalContainer
            modalType={modalType}
            modalInfo={modalInfo}
            handleCloseModal={handleCloseModal}
            handleDeleteProject={(projectID) => {
              handleCloseModal();
              deleteProject(projectID);
            }}
            handleDeleteProjects={(ids) => {
              handleCloseModal();
              deleteProjects(ids);
            }}
            handleDeleteFuse={async (fuseID) => {
              handleCloseModal();
              await deleteFuse(fuseID);
              fetchProjects();
            }}
            handleDeleteFusions={async (ids) => {
              handleCloseModal();
              await deleteFuses(ids);
              fetchProjects();
            }}
            handleDeletePitch={(pitchID) => {
              handleCloseModal();
              deletePitch(pitchID);
            }}
            handleDeletePitches={(ids) => {
              handleCloseModal();
              deletePitches(ids);
            }}
            fuseSavedData={fuseSavedData}
            fuseLoadingData={fuseLoadingData}
            fetchFuse={fetchFuse}
          />
        )}
      </div>
    </>
  );
};

export default MainLayout;
